import { useEffect, useRef } from 'react';

/** Background re-sync cadence for shell-wide lists (the Accounts list, the CRM gate). */
export const QUIET_REFRESH_INTERVAL_MS = 5 * 60_000;
/** A tab that regains focus sooner than this after the last refresh is not re-synced. */
const FOCUS_MIN_GAP_MS = 30_000;

/**
 * Calls `refresh` while `enabled`: when the browser tab becomes visible again and
 * every few minutes while it is visible. Never on navigation — callers show what
 * they already hold and swap rows in place, without a loading state.
 */
export function useQuietRefresh(refresh: () => unknown, enabled: boolean) {
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    if (!enabled) return;
    let last = Date.now();
    const run = () => {
      last = Date.now();
      void refreshRef.current();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible' && Date.now() - last >= FOCUS_MIN_GAP_MS) run();
    };
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') run();
    }, QUIET_REFRESH_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [enabled]);
}
